/**
 * Map status bar — spec §F5.10: "Status bar (26px, mono 11px): `lat, lon` mousemove ·
 * `EPSG:4326 · WGS 84` · `zoom n · ~1:scale` · spacer · `N layers on` (+ ` · clipped to
 * AOI` ochre when active) · role label · tiles note."
 *
 * The count is the ticked-and-drawn §F5.8 hierarchy rows plus the §F5.3 choropleth while an
 * indicator is mapped, plus §F5.2's thematic layers and §F5.5's overlays; `clipped to AOI`
 * arrives with §G6. Markup/ids are the mockup's `#statusbar`.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { LeafletMouseEvent } from 'leaflet';

import { ROLE_NAMES, selectYear, useAppStore } from '../../lib/state';
import { countVisible, useHierarchyStore } from './hierarchy';
import { useMap, useMapViewState } from './mapContext';
import { formatCoords, formatScale } from './statusScale';

export function StatusBar() {
  const { t } = useTranslation();
  const map = useMap();
  const { zoom, center } = useMapViewState(map);
  const [coords, setCoords] = useState<{ lat: number; lon: number } | null>(null);
  const role = useAppStore((s) => s.role);
  const country = useAppStore((s) => s.country);
  const scope = useAppStore((s) => s.scope);
  const visible = useHierarchyStore((s) => s.visible);
  const indicator = useAppStore((s) => s.indicator);
  const layersOn = useAppStore((s) => s.layersOn);
  const overlays = useAppStore((s) => s.overlays);
  const aoiClip = useAppStore((s) => s.aoiClip);
  const year = useAppStore(selectYear);
  // Rows that are actually drawn: a ticked-but-gated Communes row adds nothing to the map,
  // so it must not add to the count either. The §F5.3 choropleth is a layer too, and is on
  // exactly while an indicator is mapped; §F5.2's thematic layers are the third term and
  // §F5.5's overlays the fourth — the mockup's `updateStatus` counts
  // `layersOn.size + (indicator ? 1 : 0) + overlays.length`, and each overlay really is a
  // separate set of marks the reader can see and switch off.
  const layers =
    countVisible(visible, zoom, country, scope) +
    (indicator ? 1 : 0) +
    layersOn.size +
    overlays.length;

  useEffect(() => {
    const onMove = (e: LeafletMouseEvent) => setCoords({ lat: e.latlng.lat, lon: e.latlng.lng });
    const onOut = () => setCoords(null);
    map.on('mousemove', onMove);
    map.on('mouseout', onOut);
    return () => {
      map.off('mousemove', onMove);
      map.off('mouseout', onOut);
    };
  }, [map]);

  return (
    <div id="statusbar">
      <span id="sbCoords">{formatCoords(coords?.lat ?? null, coords?.lon ?? null)}</span>
      <span className="sep">|</span>
      <span id="sbCrs">{t('status.crs')}</span>
      <span className="sep">|</span>
      <span id="sbScale">{formatScale(zoom, center.lat)}</span>
      <span className="grow" />
      {/* Not in §F5.10's own list: the year the map is showing, so that a paused time bar
          (or a dragged-away one) never leaves the reader guessing. NOTES #193. */}
      {indicator ? (
        <>
          <span id="sbYear">
            <b>{year}</b>
          </span>
          <span className="sep">|</span>
        </>
      ) : null}
      <span id="sbLayers">
        <b>{layers}</b> {layers === 1 ? t('status.layerOn') : t('status.layersOn')}
        {/* §F5.8: the filter changes what every number on the map is *of*, so it is
            announced in the same breath as the clip — the two are the only things here
            that make the map a statement about somewhere smaller than the country. */}
        {scope ? (
          <>
            {' · '}
            <b id="sbScope">{t('status.filteredTo', { name: scope.name })}</b>
          </>
        ) : null}
        {/* §F5.10/§G6: the clip changes what the numbers on the map mean, so it is
            announced next to the count rather than buried in the tool column. */}
        {aoiClip ? (
          <>
            {' · '}
            <b id="sbClip">{t('status.clippedToAoi')}</b>
          </>
        ) : null}
      </span>
      <span className="sep">|</span>
      <span id="sbRole">{t(`role.${role}`, ROLE_NAMES[role])}</span>
      <span className="sep">|</span>
      <span id="sbTiles">{t('status.tiles')}</span>
    </div>
  );
}

export default StatusBar;
